import { supabase } from '../supabase'

// API configuration
export const API_URL =
  import.meta.env.VITE_API_URL ||
  (window.location.hostname === "dnce.club"
    ? "https://api.dnce.club"
    : window.location.hostname === "staging.dnce.club"
      ? "https://api-staging.dnce.club"
      : "http://localhost:3001")

export const APP_URL = import.meta.env.VITE_APP_URL || "http://localhost:3000"

interface RequestConfig extends RequestInit {
  skipAuth?: boolean
}

class ApiClient {
  private async getAuthHeader(): Promise<string | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      return session?.access_token ? `Bearer ${session.access_token}` : null
    } catch {
      return null
    }
  }

  private async request<T = unknown>(
    path: string,
    config: RequestConfig = {}
  ): Promise<T> {
    const { skipAuth = false, headers = {}, ...restConfig } = config

    // Get auth header unless explicitly skipped
    const authHeader = skipAuth ? null : await this.getAuthHeader()

    const response = await fetch(`${API_URL}${path}`, {
      ...restConfig,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(authHeader && { Authorization: authHeader }),
        ...headers,
      },
    })

    // Handle 401 - attempt token refresh
    if (response.status === 401 && !skipAuth) {
      const { data: { session }, error } = await supabase.auth.refreshSession()
      
      if (!error && session) {
        // Retry request with new token
        const newAuthHeader = `Bearer ${session.access_token}`
        const retryResponse = await fetch(`${API_URL}${path}`, {
          ...restConfig,
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: newAuthHeader,
            ...headers,
          },
        })
        
        if (!retryResponse.ok) {
          const contentType = retryResponse.headers.get("content-type")
          if (contentType?.includes("application/json")) {
            const error = await retryResponse.json()
            throw new Error(error.error || `Request failed: ${retryResponse.statusText}`)
          }
          throw new Error(`Request failed: ${retryResponse.status} ${retryResponse.statusText}`)
        }
        
        return retryResponse.json()
      }
    }

    if (!response.ok) {
      const contentType = response.headers.get("content-type")
      if (contentType?.includes("application/json")) {
        const error = await response.json()
        // Use the message field if available, then Zod validation details, then fall back to error field
        const zodDetail = Array.isArray(error.details) && error.details[0]?.message ? error.details[0].message : null
        const errorMessage = error.message || zodDetail || (typeof error.error === 'string' ? error.error : error.error?.message) || `Request failed: ${response.statusText}`
        throw new Error(errorMessage)
      }
      throw new Error(`Request failed: ${response.status} ${response.statusText}`)
    }

    // Handle empty responses
    const contentLength = response.headers.get("content-length")
    if (contentLength === "0" || response.status === 204) {
      return {} as T
    }

    return response.json()
  }

  async get<T = unknown>(path: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(path, { ...config, method: "GET" })
  }

  async post<T = unknown>(path: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>(path, {
      ...config,
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async put<T = unknown>(path: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>(path, {
      ...config,
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async delete<T = unknown>(path: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(path, { ...config, method: "DELETE" })
  }

  async patch<T = unknown>(path: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>(path, {
      ...config,
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    })
  }
}

// Export a singleton instance
export const api = new ApiClient()

// Keep the old apiClient export for backward compatibility during migration
export const apiClient = {
  get: (path: string, token?: string) => api.get(path, token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  post: (path: string, data: unknown, token?: string) => api.post(path, data, token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  put: (path: string, data: unknown, token?: string) => api.put(path, data, token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  delete: (path: string, token?: string) => api.delete(path, token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
}